import React, { useState, useEffect } from 'react';
import { useTranslate } from '../../Hook/useTranslate';
import api from '../../config/api';
import './RestaurantFiltersModal.css';

const RestaurantFiltersModal = ({ isOpen, onClose, initialFilters, onApply }) => {
  const [filters, setFilters] = useState(initialFilters);
  const [dynamicCategories, setDynamicCategories] = useState([]);
  const t = useTranslate();

  useEffect(() => {
    if (isOpen) {
      setFilters({
        subCategories: [],
        ...initialFilters
      });

      // Try fetching active filter categories from API
      const fetchFilters = async () => {
        try {
          const res = await api.get('/admin/filters');
          const catList = res.data?.categories || (Array.isArray(res.data) ? res.data : []);
          if (Array.isArray(catList)) {
            const activeCats = catList.filter(c => c.isActive !== false);
            setDynamicCategories(activeCats);
          }
        } catch (err) {
          console.warn("Filter categories fetch failed:", err);
          setDynamicCategories([]);
        }
      };

      fetchFilters();
    }
  }, [isOpen, initialFilters]);

  if (!isOpen) return null;

  const handleSortChange = (value) => {
    setFilters({ ...filters, sortBy: value });
  };

  const handleCheckboxChange = (field) => {
    setFilters({ ...filters, [field]: !filters[field] });
  };

  const handleRadioChange = (field, value) => {
    setFilters({ ...filters, [field]: filters[field] === value ? null : value });
  };

  const handleArrayChange = (field, value) => {
    const current = filters[field] || [];
    if (current.includes(value)) {
      setFilters({ ...filters, [field]: current.filter(item => item !== value) });
    } else {
      setFilters({ ...filters, [field]: [...current, value] });
    }
  };

  const handleClearAll = () => {
    setFilters({
      sortBy: 'Relevance',
      fastDelivery: false,
      rating: null,
      offers: false,
      costRange: [],
      foodType: [],
      pureVeg: false,
      openNow: false,
      newRestaurants: false,
      subCategories: [],
    });
  };

  const handleApply = () => {
    onApply(filters);
    onClose();
  };

  return (
    <div className="filter-modal-overlay" onClick={onClose}>
      <div className="filter-modal-content" onClick={e => e.stopPropagation()}>
        <div className="filter-modal-header">
          <h2>{t("Filters & Sorting")}</h2>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        <div className="filter-modal-body">
          {/* Sort By */}
          <div className="filter-section">
            <h3>{t("sortBy")}</h3>
            <div className="filter-options-grid">
              {['Relevance', 'Delivery Time', 'Rating', 'Cost Low to High', 'Cost High to Low'].map(option => (
                <button
                  key={option}
                  className={`filter-chip ${filters.sortBy === option ? 'selected' : ''}`}
                  onClick={() => handleSortChange(option)}
                >
                  {t(option)}
                </button>
              ))}
            </div>
          </div>

          <div className="filter-divider"></div>

          {/* Quick Filters */}
          <div className="filter-section">
            <h3>{t("quickFilters")}</h3>
            <div className="filter-options-grid">
              <button
                className={`filter-chip ${filters.fastDelivery ? 'selected' : ''}`}
                onClick={() => handleCheckboxChange('fastDelivery')}
              >
                {t("fastDeliveryLimit")}
              </button>
              <button
                className={`filter-chip ${filters.offers ? 'selected' : ''}`}
                onClick={() => handleCheckboxChange('offers')}
              >
                {t("offersBogoFree")}
              </button>
              <button
                className={`filter-chip ${filters.pureVeg ? 'selected' : ''}`}
                onClick={() => handleCheckboxChange('pureVeg')}
              >
                {t("Pure Veg")}
              </button>
              <button
                className={`filter-chip ${filters.openNow ? 'selected' : ''}`}
                onClick={() => handleCheckboxChange('openNow')}
              >
                {t("Open Now")}
              </button>
              <button
                className={`filter-chip ${filters.newRestaurants ? 'selected' : ''}`}
                onClick={() => handleCheckboxChange('newRestaurants')}
              >
                {t("newRestaurants")}
              </button>
            </div>
          </div>

          <div className="filter-divider"></div>

          {/* DB-driven Filter Categories from Admin */}
          {dynamicCategories.length > 0 && dynamicCategories.map(cat => {
            const activeSubs = (cat.subcategories || []).filter(s => s.isActive !== false);
            return (
              <React.Fragment key={cat._id || cat.name}>
                <div className="filter-section">
                  <h3>{cat.name}</h3>
                  <div className="filter-options-grid">
                    {activeSubs.length > 0 ? (
                      activeSubs.map(sub => (
                        <button
                          key={sub._id || sub.name}
                          className={`filter-chip ${(filters.subCategories || []).includes(sub.name) ? 'selected' : ''}`}
                          onClick={() => handleArrayChange('subCategories', sub.name)}
                        >
                          {sub.name}
                        </button>
                      ))
                    ) : (
                      <button
                        className={`filter-chip ${(filters.subCategories || []).includes(cat.name) ? 'selected' : ''}`}
                        onClick={() => handleArrayChange('subCategories', cat.name)}
                      >
                        {cat.name}
                      </button>
                    )}
                  </div>
                </div>
                <div className="filter-divider"></div>
              </React.Fragment>
            );
          })}

          {/* Rating */}
          <div className="filter-section">
            <h3>{t("rating")}</h3>
            <div className="filter-options-grid">
              <button
                className={`filter-chip ${filters.rating === '4.5' ? 'selected' : ''}`}
                onClick={() => handleRadioChange('rating', '4.5')}
              >
                {t("4.5+")}
              </button>
              <button
                className={`filter-chip ${filters.rating === '4.0' ? 'selected' : ''}`}
                onClick={() => handleRadioChange('rating', '4.0')}
              >
                {t("4.0+")}
              </button>
            </div>
          </div>
        </div>

        <div className="filter-modal-footer">
          <button className="clear-all-btn" onClick={handleClearAll}>{t("clearAll")}</button>
          <button className="apply-btn" onClick={handleApply}>{t("applyFilters")}</button>
        </div>
      </div>
    </div>
  );
};

export default RestaurantFiltersModal;